import React, { useState, useEffect } from 'react';
import './IdeaAtlas.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Theme categories for filtering - based on the reorganized 160 nodes
const THEME_CATEGORIES = [
  { id: 'all', label: 'All Ideas', icon: '🌐', keywords: [] },
  { id: 'power', label: 'Power & Authority', icon: '👑', keywords: ['power', 'authority', 'crown', 'throne', 'rule', 'nobility', 'council', 'storm', 'instrument'] },
  { id: 'justice', label: 'Justice & Law', icon: '⚖️', keywords: ['justice', 'law', 'judgment', 'witness', 'blade', 'scale', 'perform'] },
  { id: 'truth', label: 'Truth & Deception', icon: '🎭', keywords: ['truth', 'lie', 'deception', 'mask', 'secret', 'shadow', 'light', 'vice', 'virtue'] },
  { id: 'love', label: 'Love & Desire', icon: '❤️', keywords: ['love', 'desire', 'passion', 'lust', 'conscience', 'hunger', 'sacrifice'] },
  { id: 'knowledge', label: 'Knowledge & Reason', icon: '🧠', keywords: ['knowledge', 'reason', 'wisdom', 'ignorance', 'sight', 'lamp', 'tree', 'judgment'] },
  { id: 'time', label: 'Time & Change', icon: '⏰', keywords: ['time', 'change', 'age', 'youth', 'past', 'voyage', 'stage'] },
  { id: 'death', label: 'Life & Death', icon: '💀', keywords: ['death', 'life', 'fate', 'destiny', 'dream', 'sleep', 'doorway', 'support'] },
  { id: 'honor', label: 'Honor & Virtue', icon: '🛡️', keywords: ['honor', 'virtue', 'reputation', 'garment', 'shield', 'temptation'] },
  { id: 'nature', label: 'Nature & Elements', icon: '🌿', keywords: ['nature', 'storm', 'heaven', 'mood', 'character', 'mother', 'beast'] },
  { id: 'body', label: 'Body & Soul', icon: '🫀', keywords: ['body', 'soul', 'heart', 'mind', 'prison', 'drum', 'garden', 'sign'] },
  { id: 'emotion', label: 'Emotions', icon: '😢', keywords: ['fear', 'grief', 'anger', 'artfulness', 'perception', 'resolve'] },
  { id: 'value', label: 'Value & Corruption', icon: '💰', keywords: ['gold', 'money', 'corruption', 'value', 'virtue', 'means', 'end'] },
  { id: 'language', label: 'Language & Communication', icon: '💬', keywords: ['word', 'silence', 'weapon', 'wall', 'language'] },
  { id: 'war', label: 'War & Conflict', icon: '⚔️', keywords: ['war', 'peace', 'revenge', 'conflict', 'dove', 'tempest', 'force'] },
  { id: 'fate', label: 'Fate & Stars', icon: '⭐', keywords: ['fate', 'star', 'fortune', 'govern', 'destiny', 'circumstance'] },
];

// Minimum relevance threshold for displaying passages
const MIN_RELEVANCE_THRESHOLD = 0.3;

const IdeaAtlas = () => {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [passages, setPassages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedPassage, setExpandedPassage] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  // Load all nodes on mount
  useEffect(() => {
    loadNodes();
  }, []);

  // Filtered nodes based on selected theme
  const filteredNodes = React.useMemo(() => {
    if (selectedTheme === 'all') {
      return nodes;
    }
    
    const category = THEME_CATEGORIES.find(cat => cat.id === selectedTheme);
    if (!category || !category.keywords.length) {
      return nodes;
    }
    
    const matchedNodes = nodes.filter(node => {
      const slug = (node.slug || '').toLowerCase();
      const title = (node.title || '').toLowerCase();
      const combined = slug + ' ' + title;
      
      return category.keywords.some(keyword => combined.includes(keyword));
    });
    
    // Deduplicate by slug
    const seen = new Set();
    return matchedNodes.filter(node => {
      const slug = node.slug;
      if (seen.has(slug)) {
        return false;
      }
      seen.add(slug);
      return true;
    });
  }, [nodes, selectedTheme]);

  const loadNodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/atlas/nodes`);
      if (response.ok) {
        const data = await response.json();
        setNodes(data.nodes || []);
      }
    } catch (error) {
      console.error('Failed to load nodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim()) {
      loadNodes();
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/atlas/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });

      if (response.ok) {
        const data = await response.json();
        setNodes(data.results || []);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleThemeClick = (themeId) => {
    if (themeId === 'all') {
      setSelectedTheme('all');
      setFiltersExpanded(true);
    } else if (selectedTheme === themeId) {
      setSelectedTheme('all');
      setFiltersExpanded(true);
    } else {
      setSelectedTheme(themeId);
      setFiltersExpanded(false);
    }
  };

  const handleNodeClick = async (node) => {
    setSelectedNode(node);
    setPassages([]);
    setExpandedPassage(null);
    
    // Load enriched passages directly (motives are invisible plumbing)
    setLoading(true);
    try {
      const cacheBust = Date.now();
      const passagesRes = await fetch(
        `${BACKEND_URL}/api/atlas/node/${node.slug}/passages/enriched?_=${cacheBust}`
      );
      
      if (passagesRes.ok) {
        const data = await passagesRes.json();
        // Filter passages by relevance threshold (but always keep curated ones)
        const relevantPassages = (data.passages || []).filter(p => {
          // Curated exemplars always pass
          if (p.sources?.includes('curated')) return true;
          
          // Otherwise check relevance
          const hasKeyWords = p.key_words && p.key_words.length > 0;
          const hasHighImportance = p.key_words?.some(kw => kw.importance >= 0.7);
          const hasGoodSemanticScore = p.semantic_score >= MIN_RELEVANCE_THRESHOLD;
          return hasKeyWords && (hasHighImportance || hasGoodSemanticScore);
        });
        setPassages(relevantPassages);
      }
    } catch (error) {
      console.error('Failed to load passages:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlayTitle = (workId) => {
    const playMap = {
      'hamlet': 'Hamlet',
      'macbeth': 'Macbeth',
      'othello': 'Othello',
      'lear': 'King Lear',
      'romeo': 'Romeo and Juliet',
      'tempest': 'The Tempest',
      'midsummer': "A Midsummer Night's Dream",
      'merchant': 'The Merchant of Venice',
      'shrew': 'The Taming of the Shrew',
      'twelfth': 'Twelfth Night',
      'julius': 'Julius Caesar',
      'antony': 'Antony and Cleopatra',
      '1henryiv': 'Henry IV Part 1',
      '2henryiv': 'Henry IV Part 2',
      'henryv': 'Henry V',
      '1henryvi': 'Henry VI Part 1',
      '2henryvi': 'Henry VI Part 2',
      '3henryvi': 'Henry VI Part 3',
      'richardii': 'Richard II',
      'richardiii': 'Richard III',
      'henryviii': 'Henry VIII',
      'measure': 'Measure for Measure',
      'much_ado': 'Much Ado About Nothing',
      'alls_well': "All's Well That Ends Well",
      'as_you': 'As You Like It',
      'winters': "The Winter's Tale",
      'coriolanus': 'Coriolanus',
      'timon': 'Timon of Athens',
      'titus': 'Titus Andronicus',
      'troilus': 'Troilus and Cressida',
      'cymbeline': 'Cymbeline',
      'pericles': 'Pericles',
      'errors': 'The Comedy of Errors',
      'loves': "Love's Labour's Lost",
      'two_gents': 'The Two Gentlemen of Verona',
      'john': 'King John'
    };
    
    if (!workId) return '';
    
    const lower = workId.toLowerCase();
    // Exact match first
    if (playMap[lower]) {
      return playMap[lower];
    }
    // Fallback to partial match (sorted by key length desc to avoid 'henryv' matching 'henryviii')
    const sortedKeys = Object.keys(playMap).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
      if (lower.includes(key)) {
        return playMap[key];
      }
    }
    
    return workId.charAt(0).toUpperCase() + workId.slice(1);
  };

  const getNodeIcon = (node) => {
    if (node.emoji) {
      return node.emoji;
    }
    
    const slug = (node.slug || '').toLowerCase();
    const title = (node.title || '').toLowerCase();
    const combined = slug + ' ' + title;
    
    // Pattern matching for icons
    if (combined.match(/time|clock|hour|moment|age|season|year/)) return '⏰';
    if (combined.match(/change|transform|shift|turn/)) return '🔄';
    if (combined.match(/king|queen|crown|throne|power|rule/)) return '👑';
    if (combined.match(/war|battle|fight|combat/)) return '🗡️';
    if (combined.match(/peace|treaty|truce/)) return '🕊️';
    if (combined.match(/love|heart|passion|desire/)) return '❤️';
    if (combined.match(/truth|honest|sincere/)) return '✨';
    if (combined.match(/lie|deceiv|false|cheat|vice/)) return '🎭';
    if (combined.match(/death|die|grave|tomb/)) return '💀';
    if (combined.match(/ghost|spirit|phantom/)) return '👻';
    if (combined.match(/justice|court|judge|trial/)) return '⚖️';
    if (combined.match(/mind|thought|think|reason/)) return '🧠';
    if (combined.match(/wisdom|wise|sage/)) return '🦉';
    if (combined.match(/dream|sleep|vision/)) return '💭';
    if (combined.match(/garden|flower|bloom/)) return '🌸';
    if (combined.match(/storm|thunder|lightning/)) return '⚡';
    if (combined.match(/sun|light|day|dawn/)) return '☀️';
    if (combined.match(/moon|night|dark|shadow/)) return '🌙';
    if (combined.match(/star|heaven|sky|fate/)) return '⭐';
    if (combined.match(/lion|beast|wolf/)) return '🦁';
    if (combined.match(/serpent|snake/)) return '🐍';
    if (combined.match(/gold|treasure|wealth|value/)) return '💰';
    if (combined.match(/blood|bleed/)) return '🩸';
    if (combined.match(/instrument|means|end/)) return '🔧';
    if (combined.match(/secret|hidden|covert/)) return '🔒';
    if (combined.match(/support|life|sustain/)) return '🌱';
    
    return '💡';
  };

  // Render highlighted text with yellow keywords
  const renderHighlightedText = (passage) => {
    const text = passage.highlighted_text || passage.text || '';
    // Convert word to <mark> tags for consistent yellow highlighting
    const html = text.replace(/\*\*(\w+)\*\*/g, '<mark class="keyword-highlight">$1</mark>');
    return { __html: html };
  };

  return (
    <div className="idea-atlas">
      {/* Header */}
      <div className="atlas-header">
        <h1>Knowledge Map</h1>
        <p className="atlas-subtitle">
          Explore Shakespeare's dramatic intelligence through ideas and the imagery that embodies them
        </p>
        
        {/* Search Bar */}
        <div className="atlas-search">
          <input
            type="text"
            placeholder="Search ideas... (e.g., 'political power', 'deception', 'mortality')"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
          />
          <button onClick={() => handleSearch(searchQuery)}>🔍 Search</button>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="atlas-content two-column">
        
        {/* Column 1: IDEAS (Nodes) */}
        <div className="atlas-column nodes-column">
          <div className="column-header">
            <div className="header-with-reset">
              <h2>
                💡 {selectedTheme === 'all' ? 'Ideas' : THEME_CATEGORIES.find(t => t.id === selectedTheme)?.label || 'Ideas'}
                <span className="node-count">({filteredNodes.length})</span>
              </h2>
              {selectedTheme !== 'all' && (
                <button 
                  className="btn-show-all"
                  onClick={() => handleThemeClick('all')}
                  title="Show all idea categories"
                >
                  Show All
                </button>
              )}
            </div>
          </div>

          {/* Theme Filters */}
          <div className={`theme-filters ${!filtersExpanded ? 'collapsed' : ''}`}>
            {THEME_CATEGORIES.map(theme => {
              if (theme.id !== 'all' && !filtersExpanded) {
                return null;
              }
              if (theme.id === 'all' && !filtersExpanded) {
                return null;
              }
              return (
                <button
                  key={theme.id}
                  className={`theme-filter ${selectedTheme === theme.id ? 'active' : ''}`}
                  onClick={() => handleThemeClick(theme.id)}
                  title={theme.label}
                >
                  <span className="theme-icon">{theme.icon}</span>
                  {filtersExpanded && (
                    <span className="theme-label">{theme.label}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Node List */}
          <div className="nodes-list">
            {loading && filteredNodes.length === 0 ? (
              <div className="loading">Loading ideas...</div>
            ) : filteredNodes.length === 0 ? (
              <div className="empty-state">
                <p>No ideas match this category</p>
              </div>
            ) : (
              filteredNodes.map(node => (
                <div
                  key={node.slug}
                  className={`node-card ${selectedNode?.slug === node.slug ? 'selected' : ''}`}
                  onClick={() => handleNodeClick(node)}
                >
                  <div className="node-icon">{getNodeIcon(node)}</div>
                  <div className="node-content">
                    <div className="node-title">{node.title}</div>
                    {node.definition && (
                      <div className="node-definition">{node.definition}</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Column 2: PASSAGES (Shakespeare's Words) */}
        <div className="atlas-column passages-column">
          <div className="column-header">
            <h2>📜 Shakespeare's Words</h2>
            {selectedNode && passages.length > 0 && (
              <span className="count">{passages.length} passages</span>
            )}
          </div>

          {!selectedNode ? (
            <div className="empty-state">
              <div className="empty-icon">←</div>
              <p>Select an idea to see how Shakespeare explores it through concrete imagery and dialogue</p>
            </div>
          ) : (
            <>
              {/* Selected Node Summary */}
              <div className="selected-node-summary">
                <div className="summary-icon">{getNodeIcon(selectedNode)}</div>
                <div className="summary-content">
                  <h3>{selectedNode.title}</h3>
                  <p className="summary-definition">{selectedNode.definition}</p>
                </div>
              </div>

              {/* Passages List */}
              <div className="passages-list">
                {loading ? (
                  <div className="loading">Finding relevant passages...</div>
                ) : passages.length === 0 ? (
                  <div className="empty-state">
                    <p>No passages meet the relevance threshold for this idea</p>
                  </div>
                ) : (
                  passages.map((passage, idx) => (
                    <div key={idx} className="passage-card">
                      <div className="passage-header">
                        <div className="passage-meta-left">
                          <span className="passage-cite">{passage.cite}</span>
                          <span className="passage-speaker">{passage.speaker}</span>
                        </div>
                        <div className="passage-meta-right">
                          {/* Source badges showing what retrieval paths found this */}
                          <div className="passage-sources">
                            {passage.sources?.includes('curated') && (
                              <span className="source-badge curated" title="Verified Curated Exemplar" style={{backgroundColor: '#4ade80', color: '#000', fontWeight: 'bold'}}>VERIFIED</span>
                            )}
                            {passage.sources?.includes('direct') && (
                              <span className="source-badge direct" title="Found via direct semantic similarity">semantic</span>
                            )}
                            {passage.sources?.includes('motive') && (
                              <span className="source-badge motive" title="Found via motive/image cluster">motive</span>
                            )}
                            {passage.sources?.includes('keywords') && (
                              <span className="source-badge keywords" title="Found via keyword matching">keywords</span>
                            )}
                          </div>
                          <span className="passage-play">{getPlayTitle(passage.work_id)}</span>
                        </div>
                      </div>
                      
                      <div className="passage-text">
                        {/* Rationale / Reason (Contextual Bridge) */}
                        {(passage.reason || passage.intended_scene) && (
                          <div className="passage-rationale" style={{ 
                            fontSize: '0.9em', 
                            color: '#a0aec0', 
                            fontStyle: 'italic',
                            marginBottom: '12px',
                            paddingLeft: '12px',
                            borderLeft: '2px solid #4ade80',
                            lineHeight: '1.4'
                          }}>
                            {passage.reason || passage.intended_scene}
                          </div>
                        )}

                        {expandedPassage === idx ? (
                          <>
                            <div 
                              className="full-passage"
                              dangerouslySetInnerHTML={renderHighlightedText(passage)}
                            />
                            <button 
                              className="passage-toggle"
                              onClick={() => setExpandedPassage(null)}
                            >
                              Show less ↑
                            </button>
                          </>
                        ) : (
                          <>
                            <span 
                              className="passage-snippet"
                              dangerouslySetInnerHTML={{
                                __html: '"' + ((passage.highlighted_text || passage.text)?.substring(0, 200) || '')
                                  .replace(/\*\*(\w+)\*\*/g, '<mark class="keyword-highlight">$1</mark>') + '..."'
                              }}
                            />
                            <button 
                              className="passage-toggle"
                              onClick={() => setExpandedPassage(idx)}
                            >
                              Read more →
                            </button>
                          </>
                        )}
                      </div>

                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default IdeaAtlas;
